import { useIntervalEffect } from "@react-hookz/web";
import { useGetVehicleDocumentQuery } from "@/redux/apis/vehicle/vehicleApi";
import { useEditVehicleContext } from "../context";
import { getErrorMessage, getIsManualExpiryDate } from "../helpers";

interface IVehicleDocuments {
  vehicleId: string;
  insuranceId?: string;
  accreditationId?: string;
}

// Matches app-driver's behaviour: after this many polls while still PROCESSING,
// fall back to manual expiry-date entry so the user isn't hard-blocked when the
// document-review pipeline stalls.
const MAX_PROCESSING_RETRIES = 5;

const PROCESSING_FALLBACK_MESSAGE =
  "File couldn’t be verified automatically. Please manually enter the date and confirm that you are responsible for the entered date.";

export const useVehicleDocuments = ({ vehicleId, insuranceId, accreditationId }: IVehicleDocuments) => {
  const { documentRetryCount, setDocumentRetryCount } = useEditVehicleContext();
  const {
    data: insuranceDoc,
    isFetching: isLoadingInsurance,
    refetch: refetchInsurance,
  } = useGetVehicleDocumentQuery(
    {
      vehicleId,
      vehicleDocumentId: insuranceId || "",
    },
    {
      skip: !insuranceId,
    }
  );

  const {
    data: accreditationDoc,
    isFetching: isLoadingAccreditation,
    refetch: refetchAccreditation,
  } = useGetVehicleDocumentQuery(
    {
      vehicleId,
      vehicleDocumentId: accreditationId || "",
    },
    {
      skip: !accreditationId,
    }
  );

  useIntervalEffect(() => {
    if (
      insuranceId &&
      insuranceDoc?.approvalStatus === "PROCESSING" &&
      documentRetryCount.insurance < MAX_PROCESSING_RETRIES
    ) {
      refetchInsurance();
      setDocumentRetryCount((prev) => ({
        ...prev,
        insurance: prev.insurance + 1,
      }));
    }

    if (
      accreditationId &&
      accreditationDoc?.approvalStatus === "PROCESSING" &&
      documentRetryCount.accreditation < MAX_PROCESSING_RETRIES
    ) {
      refetchAccreditation();
      setDocumentRetryCount((prev) => ({
        ...prev,
        accreditation: prev.accreditation + 1,
      }));
    }
  }, 5000);

  const insuranceProcessingExhausted =
    !!insuranceId &&
    insuranceDoc?.approvalStatus === "PROCESSING" &&
    documentRetryCount.insurance >= MAX_PROCESSING_RETRIES;

  const accreditationProcessingExhausted =
    !!accreditationId &&
    accreditationDoc?.approvalStatus === "PROCESSING" &&
    documentRetryCount.accreditation >= MAX_PROCESSING_RETRIES;

  // Treat polling-exhausted-while-PROCESSING documents as needing manual entry,
  // so the expiry-date input and confirm checkbox unlock, matching app-driver.
  const insuranceManualExpiryDate =
    insuranceProcessingExhausted ||
    (!!insuranceId && !!insuranceDoc && getIsManualExpiryDate(insuranceDoc));

  const accreditationManualExpiryDate =
    accreditationProcessingExhausted ||
    (!!accreditationId && !!accreditationDoc && getIsManualExpiryDate(accreditationDoc));

  const insuranceErrorMessage = insuranceProcessingExhausted
    ? PROCESSING_FALLBACK_MESSAGE
    : insuranceId && insuranceDoc
      ? getErrorMessage(insuranceDoc)
      : "";

  const accreditationErrorMessage = accreditationProcessingExhausted
    ? PROCESSING_FALLBACK_MESSAGE
    : accreditationId && accreditationDoc
      ? getErrorMessage(accreditationDoc)
      : "";

  // Once retries are exhausted, stop reporting the doc as processing so the
  // Save button and date input can unblock.
  const isProcessing =
    (insuranceDoc?.approvalStatus === "PROCESSING" && !insuranceProcessingExhausted) ||
    (accreditationDoc?.approvalStatus === "PROCESSING" && !accreditationProcessingExhausted);

  return {
    insurance: {
      document: insuranceId ? insuranceDoc : undefined,
      errorMessage: insuranceErrorMessage,
      manualExpiryDate: insuranceManualExpiryDate,
      isLoading: isLoadingInsurance,
    },
    accreditation: {
      document: accreditationId ? accreditationDoc : undefined,
      errorMessage: accreditationErrorMessage,
      manualExpiryDate: accreditationManualExpiryDate,
      isLoading: isLoadingAccreditation,
    },
    isLoading: isLoadingInsurance || isLoadingAccreditation,
    isProcessing,
  };
};
